import { ConfirmDialog } from '@/components/ConfirmDialog';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { useDisplayPreferences } from '@/shared/hooks/useDisplayPreferences';
import { fontFamilies, s } from '@/theme/tokens';
import { Stack, Text } from '@/ui';
import { Box } from '@/ui/primitives';
import type { ReactElement } from 'react';

type DeleteTestDialogProps = {
  open: boolean;
  testName?: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const DeleteTestDialog = ({
  open,
  testName,
  loading = false,
  onClose,
  onConfirm,
}: DeleteTestDialogProps): ReactElement => {
  const { prefersMonospaceLiterals } = useDisplayPreferences();
  const resolvedTestName = testName?.trim() ?? 'this test';
  const testNameCodeSx = {
    display: 'inline',
    fontFamily: prefersMonospaceLiterals ? fontFamilies.mono : 'inherit',
    backgroundColor: 'action.hover',
    borderRadius: s('xxs'),
    pl: s('sm'),
    pr: s('xs'),
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    boxDecorationBreak: 'clone',
  } as const;

  return (
    <ConfirmDialog
      open={open}
      title={getDialogMessage('entity.confirmDelete.title')}
      description={
        <Stack gap="md">
          <Text role="body" color="textSecondary">
            Are you sure you want to delete test{' '}
            <Box component="code" sx={testNameCodeSx}>
              {resolvedTestName}
            </Box>
            ?
          </Text>
          <Text role="body" color="textSecondary">
            This action will also delete all test runs associated with this test.
          </Text>
        </Stack>
      }
      confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'test' })}
      confirmColor="red"
      loading={loading}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
};
